/**
 * Challenge API utility functions
 * Centralized functions for interacting with the WordPress Challenge Join Endpoints plugin
 */

// WordPress site root (no trailing slash). If NEXT_PUBLIC_BASE_URL already ends with `/api`,
// strip it so we never call .../api/api/challenges/v1 (WordPress returns rest_no_route).
function getWordPressSiteRoot() {
  let b = (process.env.NEXT_PUBLIC_BASE_URL || '').trim().replace(/\/+$/, '');
  if (/\/api$/i.test(b)) {
    return b.slice(0, -4);
  }
  return b;
}

// WordPress REST base for challenge routes (theme uses rest_url_prefix `api` on production).
// Example: NEXT_PUBLIC_BASE_URL=https://site.figmawya.com/
const BASE_URL = `${getWordPressSiteRoot()}/api/challenges/v1`;


/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Join a challenge
 * @param {number} challengeId - The challenge post ID
 * @param {string} identifier - User identifier (ID, nicename, login, or email)
 * @param {string} by - How to interpret identifier ('id', 'nicename', 'login', 'email')
 * @returns {Promise<Object>} Response data from the API
 */
export const joinChallenge = async (challengeId, identifier, by = 'id') => {
  try {
    const response = await fetch(`${BASE_URL}/join/${challengeId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ identifier: String(identifier), by }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error joining challenge:', error);
    throw error;
  }
};

/**
 * Leave a challenge
 * @param {number} challengeId - The challenge post ID
 * @param {string} identifier - User identifier (ID, nicename, login, or email)
 * @param {string} by - How to interpret identifier ('id', 'nicename', 'login', 'email')
 * @returns {Promise<Object>} Response data from the API
 */
export const leaveChallenge = async (challengeId, identifier, by = 'id') => {
  try {
    const response = await fetch(`${BASE_URL}/leave/${challengeId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ identifier: String(identifier), by }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error leaving challenge:', error);
    throw error;
  }
};

/**
 * Get list of challenge participants
 * @param {number} challengeId - The challenge post ID
 * @returns {Promise<Object>} Response data from the API
 */
export const getChallengeParticipants = async (challengeId) => {
  try {
    const response = await fetch(`${BASE_URL}/participants/${challengeId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching challenge participants:', error);
    throw error;
  }
};

/**
 * Same as getChallengeParticipants but sends the JWT when present so WordPress
 * can attach fields like chdeliverEditUrl for administrators / moderators.
 *
 * @param {number} challengeId
 * @returns {Promise<Object>}
 */
export const getChallengeParticipantsWithAuth = async (challengeId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  const response = await fetch(`${BASE_URL}/participants/${challengeId}`, { headers });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

const WP_API_BASE = `${getWordPressSiteRoot()}/api/wp/v2`;

/**
 * Fetch a single chdeliver post (staff needs JWT; include ACF + embeds).
 * @param {number|string} deliveryPostId
 * @returns {Promise<Object>}
 */
export const getChdeliverPostAuthenticated = async (deliveryPostId) => {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const url = `${WP_API_BASE}/chdeliver/${encodeURIComponent(String(deliveryPostId))}?context=edit&_embed`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}: ${response.statusText}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/** Allowed ACF `status` values on chdeliver (WordPress / ACF REST). */
export const CHDELIVER_ACF_STATUS_VALUES = Object.freeze([
  'Pending',
  'review',
  'success',
  'failed',
  'returned',
]);

/**
 * Update chdeliver ACF field `status` (administrator / commadmin).
 * POSTs to WordPress `challenges/v1/chdeliver-acf-status` (same host as join/participants) so
 * production WAF/Cloudflare rules on the Next.js `/api/*` path do not block the request.
 *
 * @param {number|string} deliveryPostId
 * @param {string} status one of CHDELIVER_ACF_STATUS_VALUES
 * @returns {Promise<Object>} Response JSON (includes acf.status)
 */
function isWpRestNoRoute(response, data) {
  if (response.status !== 404) return false;
  if (data?.code === 'rest_no_route') return true;
  const m = typeof data?.message === 'string' ? data.message : '';
  return (
    m.includes('No route was found') ||
    m.includes('no route was found') ||
    m.includes('لم يتم العثور على مسار')
  );
}

export const updateChdeliverAcfStatus = async (deliveryPostId, status) => {
  if (!CHDELIVER_ACF_STATUS_VALUES.includes(status)) {
    throw new Error('Invalid chdeliver ACF status');
  }
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const body = JSON.stringify({ id: Number(deliveryPostId), status });
  const primaryUrl = `${BASE_URL}/chdeliver-acf-status`;
  const root = getWordPressSiteRoot();
  const fallbackUrl = root ? `${root}/wp-json/challenges/v1/chdeliver-acf-status` : null;

  const doFetch = async (url) => {
    const response = await fetch(url, { method: 'POST', headers, body });
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    return { response, data };
  };

  let { response, data } = await doFetch(primaryUrl);
  if (!response.ok && fallbackUrl && isWpRestNoRoute(response, data)) {
    ({ response, data } = await doFetch(fallbackUrl));
  }

  if (!response.ok) {
    const msg =
      data?.message ||
      data?.data?.message ||
      `HTTP ${response.status}: ${response.statusText}`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }
  return data;
};

/**
 * Check if a user has joined a challenge
 * @param {number} challengeId - The challenge post ID
 * @param {number} userId - The user ID to check
 * @returns {Promise<boolean>} True if user has joined, false otherwise
 */
export const hasUserJoinedChallenge = async (challengeId, userId) => {
  try {
    const participants = await getChallengeParticipants(challengeId);
    return participants.participants.some(p => p.id == userId);
  } catch (error) {
    console.error('Error checking if user joined challenge:', error);
    return false;
  }
};

/**
 * Get current user from localStorage
 * @returns {Object|null} Current user object or null if not found
 */
export const getCurrentUser = () => {
  try {
    const userInfo = localStorage.getItem('user');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    console.error('Error parsing user info:', error);
    return null;
  }
};

/**
 * Get current user's authentication token
 * @returns {string|null} JWT token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has valid token
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  if (!token) return false;
  
  // Basic token validation (you might want to add more sophisticated validation)
  try {
    // Check if token is not expired (basic check)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    return payload.exp > now;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Join challenge with current user
 * @param {number} challengeId - The challenge post ID
 * @returns {Promise<Object>} Response data from the API
 */
export const joinChallengeAsCurrentUser = async (challengeId) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No authenticated user found');
  }
  
  return await joinChallenge(challengeId, currentUser.user_nicename, 'nicename');
};

/**
 * Leave challenge as current user
 * @param {number} challengeId - The challenge post ID
 * @returns {Promise<Object>} Response data from the API
 */
export const leaveChallengeAsCurrentUser = async (challengeId) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No authenticated user found');
  }
  
  return await leaveChallenge(challengeId, currentUser.user_nicename, 'nicename');
};

/**
 * Check if current user has joined a challenge
 * @param {number} challengeId - The challenge post ID
 * @returns {Promise<boolean>} True if current user has joined, false otherwise
 */
export const hasCurrentUserJoinedChallenge = async (challengeId) => {
  const currentUser = getCurrentUser();
  if (!currentUser) return false;
  
  return await hasUserJoinedChallenge(challengeId, currentUser.id);
};

/**
 * Get current user's participation status for a challenge
 * Returns one of: 'none' | 'pending' | 'accepted'
 * - 'pending' maps to DB status 'joined' (awaiting admin action)
 * - 'accepted' maps to DB status 'accepted'
 */
export const getCurrentUserChallengeStatus = async (challengeId) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return 'none';

    const data =
      typeof window !== 'undefined' && localStorage.getItem('token')
        ? await getChallengeParticipantsWithAuth(challengeId)
        : await getChallengeParticipants(challengeId);
    const participants = Array.isArray(data?.participants) ? data.participants : [];

    const match = participants.find((p) => {
      const byId = typeof currentUser.id !== 'undefined' && p.id == currentUser.id;
      const byNicename = (
        (p.nicename && currentUser.user_nicename && p.nicename === currentUser.user_nicename) ||
        (p.user_nicename && currentUser.user_nicename && p.user_nicename === currentUser.user_nicename)
      );
      return byId || byNicename;
    });

    if (!match) return 'none';
    if (match.status === 'accepted') return 'accepted';
    if (match.status === 'joined') return 'pending';
    return 'none';
  } catch (error) {
    console.error('Error checking current user challenge status:', error);
    return 'none';
  }
};

/**
 * Normalize ACF "challenge" value (ID, string, or post object) for comparison.
 * @param {unknown} raw
 * @returns {string|null}
 */
export const normalizeChallengeIdFromAcfField = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t === '' ? null : t;
  }
  if (typeof raw === 'object' && raw) {
    if (raw.ID != null) return String(Number(raw.ID));
    if (raw.id != null) return String(Number(raw.id));
  }
  return null;
};

/**
 * Find current user's chdeliver in a WP REST list response.
 * @param {unknown[]} items
 * @param {string|number} challengeId
 * @returns {object|null}
 */
export const findChdeliverForChallengeInList = (items, challengeId) => {
  if (!Array.isArray(items) || challengeId == null || challengeId === '') return null;
  const target = String(challengeId);
  return (
    items.find((p) => {
      const acf = p?.acf || {};
      const c =
        normalizeChallengeIdFromAcfField(acf.challenge) ||
        normalizeChallengeIdFromAcfField(acf.challenge_id) ||
        normalizeChallengeIdFromAcfField(acf.related_challenge);
      return c === target;
    }) || null
  );
};

/**
 * Current user's chdeliver for this challenge: prefers participant row chdeliverPostId (synced in WP),
 * then falls back to scanning chdeliver?author=… (handles ACF object / REST shape quirks).
 *
 * @param {string|number} challengeId
 * @returns {Promise<object|null>}
 */
export const getMyChdeliverForChallenge = async (challengeId) => {
  if (challengeId == null || challengeId === '') return null;
  const cid = String(challengeId);
  const me = getCurrentUser();
  if (!me?.id && !me?.user_nicename) return null;

  const matchParticipantRow = (list) => {
    if (!Array.isArray(list)) return null;
    return list.find((p) => {
      const byId = me.id != null && String(p.id) === String(me.id);
      const byNicename =
        (p.nicename && me.user_nicename && String(p.nicename) === String(me.user_nicename)) ||
        (p.user_nicename && me.user_nicename && String(p.user_nicename) === String(me.user_nicename));
      return byId || byNicename;
    });
  };

  try {
    const data =
      typeof window !== 'undefined' && localStorage.getItem('token')
        ? await getChallengeParticipantsWithAuth(challengeId)
        : await getChallengeParticipants(challengeId);
    const row = matchParticipantRow(data?.participants);
    const pid = row?.chdeliverPostId ?? row?.chdeliver_post_id;
    if (pid) {
      try {
        return await getChdeliverPostAuthenticated(pid);
      } catch {
        return {
          id: Number(pid),
          acf: {},
          status: row?.deliveryStatus || row?.delivery_status || 'publish',
        };
      }
    }
  } catch {
    /* participants may fail; continue to REST list */
  }

  try {
    const userId = me.id;
    if (!userId) return null;
    const headers = { Accept: 'application/json' };
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(
      `${WP_API_BASE}/chdeliver?author=${encodeURIComponent(String(userId))}&per_page=100`,
      { headers }
    );
    if (!res.ok) return null;
    const items = await res.json();
    return findChdeliverForChallengeInList(Array.isArray(items) ? items : [], cid);
  } catch (error) {
    console.error('getMyChdeliverForChallenge list fallback:', error);
    return null;
  }
};

// Export all functions
export default {
  joinChallenge,
  leaveChallenge,
  getChallengeParticipants,
  getChallengeParticipantsWithAuth,
  getChdeliverPostAuthenticated,
  CHDELIVER_ACF_STATUS_VALUES,
  updateChdeliverAcfStatus,
  hasUserJoinedChallenge,
  getCurrentUser,
  getAuthToken,
  isAuthenticated,
  joinChallengeAsCurrentUser,
  leaveChallengeAsCurrentUser,
  hasCurrentUserJoinedChallenge,
  getCurrentUserChallengeStatus,
  normalizeChallengeIdFromAcfField,
  findChdeliverForChallengeInList,
  getMyChdeliverForChallenge,
};
